// runWatchdog.js — WS-B / INV-2 (incidente tastedesign).
//
// O turno principal do renderer roda `await runLocalAgentLoop(...)` sob um
// AbortController de cancel COOPERATIVO (só Stop/ESC). Se o await TRAVA (IPC que
// nunca volta, /agent-turn que estola, stream que nunca fecha), o `finally` do
// runAgent NUNCA executa: setLocalRunning(false) não dispara e o timer da pill
// (LiveActivity) conta pra sempre — foi o "33 minutos" do incidente.
//
// Este watchdog é IDLE-based (não wall-clock): reseta a cada evento do loop (ping).
// Só dispara após `idleMs` de silêncio TOTAL — a condição real de "travado" —, então
// NÃO trunca um build longo que está progredindo (emite tokens/tool_calls). Espelha a
// staleness do server (sessionStatus STALE_RUN_MS, também idle) → pill e sidebar concordam.
//
// PURE + injetável (setTimeoutFn/clearTimeoutFn) para teste com fake timers. Sem React.

import { DAG_TASK_DEADLINE_MS } from '../../../lib/loop/taskDeadline.js';
import { ABORT_REASON, abortWithReason } from '../../../lib/loop/abortReason.js';

// Espelha apolo-api/src/services/sessionStatus.js STALE_RUN_MS (10 min): o server
// rebaixa um turn_start sem novos eventos há 10 min para needs_you; a pill deve idem.
export const RUN_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
export const DAG_RUN_IDLE_TIMEOUT_MS = DAG_TASK_DEADLINE_MS + 60 * 1000;

// O deadline específico da task precisa vencer primeiro; caso contrário o
// watchdog global transforma task_timeout em um `aborted` opaco.
export function resolveRunIdleTimeout(dagTasks) {
  return Array.isArray(dagTasks) && dagTasks.length > 0
    ? DAG_RUN_IDLE_TIMEOUT_MS
    : RUN_IDLE_TIMEOUT_MS;
}

/**
 * @param {{
 *   controller: AbortController,
 *   sessionId: string,
 *   getRunController: (id: string) => any,   // guard same-controller (run mais novo?)
 *   onTimeout: () => void,                    // cleanup: desliga pill, libera controller, msg
 *   idleMs?: number,
 *   setTimeoutFn?: typeof setTimeout,
 *   clearTimeoutFn?: typeof clearTimeout,
 * }} cfg
 * @returns {{ ping: () => void, cancel: () => void, readonly fired: boolean }}
 */
export function armRunWatchdog({
  controller,
  sessionId,
  getRunController,
  onTimeout,
  idleMs = RUN_IDLE_TIMEOUT_MS,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
}) {
  let handle = null;
  let settled = false; // disparou OU foi cancelado → não re-arma mais
  let fired = false;

  const fire = () => {
    if (settled) return;
    handle = null;
    // Já abortado por outro caminho (ex.: Stop/ESC do usuário, que aborta o controller
    // mas não alcança este watchdog local)? NÃO dispara o encerramento por idle nem posta
    // a mensagem de "travamento" — seria FALSA e contradiria uma ação explícita do usuário
    // (review WS-B #3). O finally já cuidou/cuidará da limpeza; aqui só desarmamos.
    if (controller?.signal?.aborted) {
      settled = true;
      return;
    }
    // Same-controller guard: se um run MAIS NOVO já assumiu esta sessão, não toca em
    // nada (não aborta o run novo nem apaga a pill dele). Mesmo guard do finally.
    if (typeof getRunController === 'function' && getRunController(sessionId) !== controller) {
      settled = true;
      return;
    }
    settled = true;
    fired = true;
    abortWithReason(controller, ABORT_REASON.RUN_IDLE_TIMEOUT);
    try { onTimeout?.(); } catch { /* noop — cleanup best-effort */ }
  };

  const arm = () => { handle = setTimeoutFn(fire, idleMs); };
  arm();

  return {
    ping() {
      if (settled) return;
      if (handle != null) clearTimeoutFn(handle);
      arm();
    },
    cancel() {
      if (settled) return;
      settled = true;
      if (handle != null) { clearTimeoutFn(handle); handle = null; }
    },
    get fired() { return fired; },
  };
}
